/**
 * Hooks for managing product option groups and individual options.
 *
 * The backend exposes full CRUD via `ProductOptionController` but does NOT
 * expose a dedicated GET endpoint for option groups. The list lives inside
 * `ProductDetailResponse.optionGroups` (returned by the public detail
 * endpoint). The query hook fetches that detail and projects only the
 * `optionGroups` array so editors can re-render without coupling to the
 * full product payload.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

export type OptionGroupResponse = components['schemas']['OptionGroupResponse'];
export type OptionResponse = components['schemas']['OptionResponse'];
export type CreateOptionGroupRequest =
  components['schemas']['CreateOptionGroupRequest'];
export type UpdateOptionGroupRequest =
  components['schemas']['UpdateOptionGroupRequest'];
export type CreateOptionRequest = components['schemas']['CreateOptionRequest'];
export type UpdateOptionRequest = components['schemas']['UpdateOptionRequest'];

/**
 * Query-key factory scoped to the option-groups namespace.
 *
 * `list(productId)` is granular per product so editors mounted on
 * different products do not invalidate each other. Mutations rely on
 * `list()` for invalidation; `all` exists for full-namespace clearing
 * (e.g. logout).
 */
export const optionGroupsKeys = {
  all: ['option-groups'] as const,
  list: (productId: string) =>
    [...optionGroupsKeys.all, 'list', productId] as const,
};

/**
 * Loads the option groups for a product.
 *
 * Backend has no dedicated GET — we read them off `ProductDetailResponse`
 * (`/api/v1/public/products/{id}`). The response contains the full product
 * payload; we project only `optionGroups` so the cached value matches the
 * editor's contract and re-renders are deterministic.
 */
export function useProductOptionGroups(
  productId: string | null,
): UseQueryResult<OptionGroupResponse[], Error> {
  return useQuery<OptionGroupResponse[], Error>({
    queryKey: optionGroupsKeys.list(productId ?? 'none'),
    enabled: productId !== null,
    queryFn: async () => {
      if (!productId) throw new Error('Produto inválido');
      const { data, error } = await api.GET('/api/v1/public/products/{id}', {
        params: { path: { id: productId } },
      });
      if (error) throw error as Error;
      return data?.optionGroups ?? [];
    },
  });
}

interface UpdateOptionGroupVars {
  groupId: string;
  body: UpdateOptionGroupRequest;
}

interface UpdateOptionVars {
  optionId: string;
  body: UpdateOptionRequest;
}

const invalidateGroups = (
  qc: ReturnType<typeof useQueryClient>,
  productId: string,
): void => {
  void qc.invalidateQueries({ queryKey: optionGroupsKeys.list(productId) });
};

export function useCreateOptionGroup(
  productId: string,
): UseMutationResult<OptionGroupResponse, Error, CreateOptionGroupRequest> {
  const qc = useQueryClient();
  return useMutation<OptionGroupResponse, Error, CreateOptionGroupRequest>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST(
        '/api/v1/products/{productId}/option-groups',
        {
          params: { path: { productId } },
          body,
        },
      );
      if (error) throw error as Error;
      if (!data) throw new Error('Resposta vazia do servidor');
      return data;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}

export function useUpdateOptionGroup(
  productId: string,
): UseMutationResult<OptionGroupResponse, Error, UpdateOptionGroupVars> {
  const qc = useQueryClient();
  return useMutation<OptionGroupResponse, Error, UpdateOptionGroupVars>({
    mutationFn: async ({ groupId, body }) => {
      const { data, error } = await api.PUT(
        '/api/v1/products/{productId}/option-groups/{groupId}',
        {
          params: { path: { productId, groupId } },
          body,
        },
      );
      if (error) throw error as Error;
      if (!data) throw new Error('Resposta vazia do servidor');
      return data;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}

export function useDeleteOptionGroup(
  productId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (groupId) => {
      const { error } = await api.DELETE(
        '/api/v1/products/{productId}/option-groups/{groupId}',
        {
          params: { path: { productId, groupId } },
        },
      );
      if (error) throw error as Error;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}

export function useCreateOption(
  productId: string,
  groupId: string,
): UseMutationResult<OptionResponse, Error, CreateOptionRequest> {
  const qc = useQueryClient();
  return useMutation<OptionResponse, Error, CreateOptionRequest>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST(
        '/api/v1/option-groups/{groupId}/options',
        {
          params: { path: { groupId } },
          body,
        },
      );
      if (error) throw error as Error;
      if (!data) throw new Error('Resposta vazia do servidor');
      return data;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}

export function useUpdateOption(
  productId: string,
  groupId: string,
): UseMutationResult<OptionResponse, Error, UpdateOptionVars> {
  const qc = useQueryClient();
  return useMutation<OptionResponse, Error, UpdateOptionVars>({
    mutationFn: async ({ optionId, body }) => {
      const { data, error } = await api.PUT(
        '/api/v1/option-groups/{groupId}/options/{optionId}',
        {
          params: { path: { groupId, optionId } },
          body,
        },
      );
      if (error) throw error as Error;
      if (!data) throw new Error('Resposta vazia do servidor');
      return data;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}

export function useDeleteOption(
  productId: string,
  groupId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (optionId) => {
      const { error } = await api.DELETE(
        '/api/v1/option-groups/{groupId}/options/{optionId}',
        {
          params: { path: { groupId, optionId } },
        },
      );
      if (error) throw error as Error;
    },
    onSuccess: () => invalidateGroups(qc, productId),
  });
}
